import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useCallback, useEffect, useMemo, useState } from 'react';

import defaultClient from '@src/api/clients/default';
import ClientDropdown from '@src/components/ClientDropdown';
import { fetchClientVerticalOptions } from '@src/api/actions/verticals';
import {
  previewFacebookZipTargeting,
  pushFacebookZipTargeting,
  fetchFacebookAdSetTargeting,
  fetchFacebookZipPushLog,
} from '@src/api/actions';
import { useMessage } from '@src/context/common';

function parseCampaignVertical(campaignName) {
  if (!campaignName) {
    return null;
  }
  try {
    const normalized = campaignName.startsWith('?') ? campaignName : `?${campaignName}`;
    const params = new URLSearchParams(normalized);
    const vertical = params.get('v') || params.get('vertical');
    return vertical ? vertical.trim() : null;
  } catch (error) {
    return null;
  }
}

const PREVIEW_SAMPLE_SIZE = 20;
const USD_CURRENCY_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function CampaignBuilderPage() {
  const message = useMessage();

  const [clientsError, setClientsError] = useState('');

  const [selectedClientId, setSelectedClientId] = useState(null);
  const [structure, setStructure] = useState([]);
  const [structureLoading, setStructureLoading] = useState(false);
  const [structureError, setStructureError] = useState('');

  const [territories, setTerritories] = useState([]);
  const [territoriesLoading, setTerritoriesLoading] = useState(false);
  const [territoriesError, setTerritoriesError] = useState('');
  const [budgetTerritoriesLoading, setBudgetTerritoriesLoading] = useState(false);
  const [didAutoApplyBudgetTerritories, setDidAutoApplyBudgetTerritories] = useState(false);
  const [territoryBudgetTotals, setTerritoryBudgetTotals] = useState({});
  const [budgetMonthLabel, setBudgetMonthLabel] = useState('');

  const [verticals, setVerticals] = useState([]);
  const [verticalsLoading, setVerticalsLoading] = useState(true);
  const [verticalsError, setVerticalsError] = useState('');

  const [selectedAdAccountId, setSelectedAdAccountId] = useState('');
  const [selectedCampaignIds, setSelectedCampaignIds] = useState([]);
  const [selectedAdSetIds, setSelectedAdSetIds] = useState([]);
  const [showInactiveCampaigns, setShowInactiveCampaigns] = useState(false);

  const [selectedVerticalIds, setSelectedVerticalIds] = useState([]);
  const [selectedTerritoryIds, setSelectedTerritoryIds] = useState([]);
  const [includeFilteredOut, setIncludeFilteredOut] = useState(false);

  const [preview, setPreview] = useState({ zip_count: 0, sample_zip_codes: [] });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [adSetTargeting, setAdSetTargeting] = useState(null);
  const [adSetTargetingLoading, setAdSetTargetingLoading] = useState(false);
  const [adSetTargetingError, setAdSetTargetingError] = useState('');
  const [pushLog, setPushLog] = useState([]);
  const [pushLogLoading, setPushLogLoading] = useState(false);
  const [pushLogError, setPushLogError] = useState('');

  const [pushLoading, setPushLoading] = useState(false);
  const [pushError, setPushError] = useState('');
  const [lastPush, setLastPush] = useState(null);
  const [frontendLogs, setFrontendLogs] = useState([]);

  const appendFrontendLog = (messageText, meta = {}) => {
    setFrontendLogs((prev) => [
      {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        at: new Date().toISOString(),
        message: messageText,
        meta,
      },
      ...prev,
    ].slice(0, 25));
  };

  const buildFacebookAdsManagerUrl = (accountId, adSetIds) => {
    const base = `https://adsmanager.facebook.com/adsmanager/manage/adsets?act=${accountId}`;
    if (adSetIds?.length) {
      return `${base}&selected_adset_ids=${adSetIds.join(',')}`;
    }
    return base;
  };

  useEffect(() => {
    if (!selectedClientId) {
      setVerticals([]);
      setSelectedVerticalIds([]);
      return;
    }

    setVerticalsLoading(true);
    setVerticalsError('');
    fetchClientVerticalOptions({ clientId: selectedClientId })
      .then((response) => {
        const fetched = response.data || [];
        setVerticals(fetched);
        setSelectedVerticalIds(fetched.map((vertical) => vertical.id));
      })
      .catch((error) => {
        setVerticalsError(error?.message || 'Failed to load verticals.');
      })
      .finally(() => setVerticalsLoading(false));
  }, [selectedClientId]);

  useEffect(() => {
    if (!selectedClientId) {
      setStructure([]);
      setSelectedAdAccountId('');
      setSelectedCampaignIds([]);
      setSelectedAdSetIds([]);
      return;
    }
    setStructureLoading(true);
    setStructureError('');
    defaultClient
      .request({
        url: `dashboard/facebook/ad-structure/?client_id=${selectedClientId}&include_inactive=${showInactiveCampaigns}`,
        method: 'GET',
      })
      .then((response) => {
        const fetchedStructure = response.data || [];
        setStructure(fetchedStructure);
        const campaignCount = fetchedStructure.reduce(
          (sum, account) => sum + ((account?.campaigns || []).length),
          0
        );
        const adSetCount = fetchedStructure.reduce(
          (sum, account) =>
            sum +
            (account?.campaigns || []).reduce(
              (campaignSum, campaign) => campaignSum + ((campaign?.ad_sets || []).length),
              0
            ),
          0
        );
        appendFrontendLog('Ad structure loaded', {
          accounts: fetchedStructure.length,
          campaigns: campaignCount,
          ad_sets: adSetCount,
          include_inactive: showInactiveCampaigns,
        });
      })
      .catch((error) => {
        setStructureError(error?.message || 'Failed to load Facebook structure.');
        appendFrontendLog('Ad structure failed', {
          error: error?.message || 'Failed to load Facebook structure.',
          client_id: selectedClientId,
          include_inactive: showInactiveCampaigns,
        });
      })
      .finally(() => setStructureLoading(false));
  }, [selectedClientId, showInactiveCampaigns]);

  useEffect(() => {
    if (!selectedClientId) {
      setTerritories([]);
      return;
    }
    setTerritoriesLoading(true);
    setTerritoriesError('');
    setSelectedTerritoryIds([]);
    defaultClient
      .request({
        url: `dashboard/client-territories/?client_id=${selectedClientId}`,
        method: 'GET',
      })
      .then((response) => {
        setTerritories(response.data);
      })
      .catch((error) => {
        setTerritoriesError(error?.message || 'Failed to load territories.');
      })
      .finally(() => setTerritoriesLoading(false));
  }, [selectedClientId]);

  useEffect(() => {
    if (!territories.length) {
      setSelectedTerritoryIds([]);
      return;
    }
    const withActiveZips = territories.filter(
      (territory) => (territory.active_zip_count || 0) > 0
    );
    setSelectedTerritoryIds(
      withActiveZips.length
        ? withActiveZips.map((territory) => territory.id)
        : territories.map((territory) => territory.id)
    );
  }, [territories]);

  useEffect(() => {
    setDidAutoApplyBudgetTerritories(false);
    setTerritoryBudgetTotals({});
    setBudgetMonthLabel('');
  }, [selectedClientId]);

  const loadBudgetTerritoryIdsForCurrentMonth = useCallback(async ({ auto = false } = {}) => {
    if (!selectedClientId || !territories.length) {
      return;
    }

    setBudgetTerritoriesLoading(true);
    const today = new Date();
    const month = today.getMonth() + 1;
    const year = today.getFullYear();
    const activeVerticalIds = selectedVerticalIds
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
    const activeVerticalIdSet = new Set(activeVerticalIds);
    const activeVerticalNames = verticals
      .filter((vertical) => activeVerticalIdSet.has(Number(vertical.id)))
      .map((vertical) => vertical.vertical)
      .filter(Boolean);
    setBudgetMonthLabel(today.toLocaleString('default', { month: 'long', year: 'numeric' }));
    try {
      const requestBasePayload = {
        client_id: selectedClientId,
        month,
        year,
      };
      const responses = activeVerticalIds.length
        ? await Promise.all(
            activeVerticalIds.map((verticalId) =>
              defaultClient.request({
                url: 'dashboard/client-budgets/',
                method: 'GET',
                payload: {
                  ...requestBasePayload,
                  vertical_id: verticalId,
                },
              })
            )
          )
        : [
            await defaultClient.request({
              url: 'dashboard/client-budgets/',
              method: 'GET',
              payload: requestBasePayload,
            }),
          ];

      const rows = [];
      const seenBudgetIds = new Set();
      responses.forEach((response) => {
        const responseRows = Array.isArray(response.data)
          ? response.data
          : response?.data?.results || [];
        responseRows.forEach((budget) => {
          const budgetId = Number(budget?.id);
          if (Number.isFinite(budgetId) && budgetId > 0) {
            if (seenBudgetIds.has(budgetId)) {
              return;
            }
            seenBudgetIds.add(budgetId);
          }
          rows.push(budget);
        });
      });
      const territoryIdSet = new Set();
      const totalsByTerritory = {};

      rows.forEach((budget) => {
        if (budget?.status === 'Disproved') {
          return;
        }
        const amount = Number(budget?.amount);
        if (!Number.isFinite(amount) || amount <= 0) {
          return;
        }

        const budgetTerritoryIds = new Set();
        if (budget?.location) {
          budgetTerritoryIds.add(Number(budget.location));
        }
        if (Array.isArray(budget?.location_ids)) {
          budget.location_ids.forEach((locationId) => budgetTerritoryIds.add(Number(locationId)));
        }
        if (budget?.location_data?.id) {
          budgetTerritoryIds.add(Number(budget.location_data.id));
        }
        if (Array.isArray(budget?.locations_data)) {
          budget.locations_data.forEach((item) => {
            if (item?.id) {
              budgetTerritoryIds.add(Number(item.id));
            }
          });
        }

        budgetTerritoryIds.forEach((territoryId) => {
          if (!Number.isFinite(territoryId) || territoryId <= 0) {
            return;
          }
          territoryIdSet.add(territoryId);
          totalsByTerritory[territoryId] = (totalsByTerritory[territoryId] || 0) + amount;
        });
      });
      setTerritoryBudgetTotals(totalsByTerritory);

      const validIds = territories
        .map((territory) => Number(territory.id))
        .filter((territoryId) => territoryIdSet.has(territoryId));
      if (validIds.length) {
        setSelectedTerritoryIds(validIds);
        if (!auto) {
          const filterSuffix = activeVerticalNames.length
            ? ` in ${activeVerticalNames.join(', ')}`
            : '';
          message.success(
            `Loaded ${validIds.length} budgeted territor${validIds.length === 1 ? 'y' : 'ies'} for ${today.toLocaleString('default', { month: 'long' })}${filterSuffix}.`
          );
        }
      } else if (auto) {
        const withActiveZips = territories.filter(
          (territory) => (territory.active_zip_count || 0) > 0
        );
        setSelectedTerritoryIds(
          withActiveZips.length
            ? withActiveZips.map((territory) => territory.id)
            : territories.map((territory) => territory.id)
        );
      } else {
        const filterSuffix = activeVerticalNames.length
          ? ` in ${activeVerticalNames.join(', ')}`
          : '';
        message.info(`No budgeted territories found for ${today.toLocaleString('default', { month: 'long' })}${filterSuffix}.`);
      }
    } catch (error) {
      if (!auto) {
        message.error(error?.message || 'Failed to load budgeted territories.');
      }
    } finally {
      setBudgetTerritoriesLoading(false);
      if (auto) {
        setDidAutoApplyBudgetTerritories(true);
      }
    }
  }, [selectedClientId, selectedVerticalIds, territories, verticals, message]);

  useEffect(() => {
    if (!selectedClientId || !territories.length || didAutoApplyBudgetTerritories) {
      return;
    }
    loadBudgetTerritoryIdsForCurrentMonth({ auto: true });
  }, [
    selectedClientId,
    territories,
    didAutoApplyBudgetTerritories,
    loadBudgetTerritoryIdsForCurrentMonth,
  ]);

  useEffect(() => {
    if (!structure.length) {
      setSelectedAdAccountId('');
      return;
    }
    const defaultAccount = structure[0];
    setSelectedAdAccountId((prev) => prev || defaultAccount.account_id);
  }, [structure]);

  const selectedAccount = useMemo(
    () => structure.find((account) => account.account_id === selectedAdAccountId) || null,
    [structure, selectedAdAccountId]
  );

  useEffect(() => {
    const campaigns = selectedAccount?.campaigns || [];
    if (!campaigns.length) {
      setSelectedCampaignIds([]);
      return;
    }
    setSelectedCampaignIds((prev) => {
      const validPrev = prev.filter((id) =>
        campaigns.some((campaign) => campaign.campaign_id === id)
      );
      if (validPrev.length) {
        return validPrev;
      }
      const firstWithAdSets = campaigns.find(
        (campaign) => (campaign?.ad_sets || []).length > 0
      );
      return [firstWithAdSets?.campaign_id || campaigns[0].campaign_id];
    });
  }, [selectedAccount]);

  const selectedCampaigns = useMemo(
    () =>
      (selectedAccount?.campaigns || []).filter((campaign) =>
        selectedCampaignIds.includes(campaign.campaign_id)
      ),
    [selectedAccount, selectedCampaignIds]
  );

  const adSetOptions = useMemo(() => {
    const seen = {};
    selectedCampaigns.forEach((campaign) => {
      (campaign.ad_sets || []).forEach((adSet) => {
        if (adSet?.ad_set_id && !seen[adSet.ad_set_id]) {
          seen[adSet.ad_set_id] = adSet;
        }
      });
    });
    return Object.values(seen);
  }, [selectedCampaigns]);

  useEffect(() => {
    if (!selectedCampaigns.length || !verticals.length) {
      return;
    }
    const targetVertNames = new Set();
    selectedCampaigns.forEach((campaign) => {
      const verticalName = parseCampaignVertical(campaign.campaign_name);
      if (verticalName) {
        targetVertNames.add(verticalName.toLowerCase());
      }
    });
    if (!targetVertNames.size) {
      return;
    }
    const matched = verticals.filter((vertical) =>
      targetVertNames.has((vertical.vertical || '').toLowerCase())
    );
    if (!matched.length) {
      return;
    }
    setSelectedVerticalIds(matched.map((vertical) => vertical.id));
  }, [selectedCampaigns, verticals]);

  useEffect(() => {
    if (!adSetOptions.length) {
      setSelectedAdSetIds([]);
      return;
    }
    setSelectedAdSetIds((prev) => {
      const filtered = prev.filter((id) =>
        adSetOptions.some((adSet) => adSet.ad_set_id === id)
      );
      return filtered.length ? filtered : [adSetOptions[0].ad_set_id];
    });
  }, [adSetOptions]);

  useEffect(() => {
    if (!selectedClientId) {
      setPreview({ zip_count: 0, sample_zip_codes: [] });
      setPreviewError('');
      return;
    }

    setPreviewLoading(true);
    setPreviewError('');

    previewFacebookZipTargeting({
      clientId: selectedClientId,
      verticalIds: selectedVerticalIds,
      territoryIds: selectedTerritoryIds,
      includeFilteredOut,
      sampleSize: PREVIEW_SAMPLE_SIZE,
    })
      .then((response) => {
        setPreview(response.data);
      })
      .catch((error) => {
        setPreviewError(error?.message || 'Failed to preview ZIPs.');
        setPreview({ zip_count: 0, sample_zip_codes: [] });
      })
      .finally(() => {
        setPreviewLoading(false);
      });
  }, [selectedClientId, selectedVerticalIds, selectedTerritoryIds, includeFilteredOut]);

  useEffect(() => {
    if (!selectedClientId || !selectedAdAccountId || selectedAdSetIds.length !== 1) {
      setAdSetTargeting(null);
      setAdSetTargetingError('');
      return;
    }

    const adSetId = selectedAdSetIds[0];
    setAdSetTargetingLoading(true);
    setAdSetTargetingError('');
    fetchFacebookAdSetTargeting({
      clientId: selectedClientId,
      adAccountId: selectedAdAccountId,
      adSetId,
    })
      .then((response) => {
        setAdSetTargeting(response.data);
      })
      .catch((error) => {
        setAdSetTargeting(null);
        setAdSetTargetingError(error?.message || 'Failed to load ad set targeting.');
      })
      .finally(() => setAdSetTargetingLoading(false));
  }, [selectedClientId, selectedAdAccountId, selectedAdSetIds]);

  const loadPushLog = (options = {}) => {
    if (!selectedClientId) {
      setPushLog([]);
      return;
    }
    setPushLogLoading(true);
    setPushLogError('');
    fetchFacebookZipPushLog({
      clientId: selectedClientId,
      adSetId: options.adSetId,
      limit: options.limit || 25,
    })
      .then((response) => {
        setPushLog(response.data || []);
      })
      .catch((error) => {
        setPushLog([]);
        setPushLogError(error?.message || 'Failed to load push log.');
      })
      .finally(() => setPushLogLoading(false));
  };

  useEffect(() => {
    loadPushLog();
  }, [selectedClientId]);

  const handlePush = async () => {
    if (!selectedClientId || !selectedAdAccountId || !selectedAdSetIds.length) {
      return;
    }

    setPushLoading(true);
    setPushError('');
    setLastPush(null);
    appendFrontendLog('Push started', {
      client_id: selectedClientId,
      ad_account_id: selectedAdAccountId,
      ad_set_ids: selectedAdSetIds,
      include_filtered_out: includeFilteredOut,
      preview_zip_count: preview.zip_count || 0,
    });

    try {
      const selectedAdSets = adSetOptions.filter((adSet) =>
        selectedAdSetIds.includes(adSet.ad_set_id)
      );
      const adSetNames = selectedAdSets.map((adSet) => adSet.ad_set_name || '');
      const response = await pushFacebookZipTargeting({
        client_id: selectedClientId,
        ad_account_id: selectedAdAccountId,
        ad_set_ids: selectedAdSetIds,
        ad_set_names: adSetNames,
        vertical_ids: selectedVerticalIds,
        territory_ids: selectedTerritoryIds,
        include_filtered_out: includeFilteredOut,
        source_type: 'campaign_builder_ui',
        source_id: `campaign-builder-${Date.now()}`,
        csv_name: `Campaign Builder ${new Date().toISOString()}`,
      });

      const resultCount =
        response?.data?.results?.length || selectedAdSetIds.length;
      appendFrontendLog('Push succeeded', {
        ad_account_id: response?.data?.ad_account_id || selectedAdAccountId,
        ad_set_count: resultCount,
        requested_zip_count: response?.data?.zip_count,
        pushed_zip_count: response?.data?.pushed_zip_count,
        invalid_zip_codes: response?.data?.invalid_zip_codes || [],
      });
      setLastPush({
        adAccountId: response?.data?.ad_account_id || selectedAdAccountId,
        adSetIds: [...selectedAdSetIds],
      });
      message.success(
        `ZIP targeting pushed to ${resultCount} ad set${resultCount === 1 ? '' : 's'} (${preview.zip_count || response.data.zip_count} zips).`
      );
      loadPushLog({ adSetId: selectedAdSetIds.length === 1 ? selectedAdSetIds[0] : undefined });
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || 'Failed to push ZIP targeting.';
      setPushError(detail);
      appendFrontendLog('Push failed', {
        error: detail,
        client_id: selectedClientId,
        ad_account_id: selectedAdAccountId,
        ad_set_ids: selectedAdSetIds,
      });
      message.error(detail);
    } finally {
      setPushLoading(false);
    }
  };

  const accountOptions = structure;
  const campaignOptions = selectedAccount?.campaigns || [];
  const territoryBudgetItems = useMemo(() => (
    territories
      .map((territory) => ({
        id: Number(territory.id),
        name: territory.name || territory.territory || `Territory ${territory.id}`,
        budget: Number(territoryBudgetTotals[Number(territory.id)] || 0),
      }))
      .filter((item) => Number.isFinite(item.budget) && item.budget > 0)
      .sort((a, b) => b.budget - a.budget)
  ), [territories, territoryBudgetTotals]);

  const previewChips = preview.sample_zip_codes.map((zip) => (
    <Chip key={zip} label={zip} size="small" />
  ));

  return (
    <Box>
      <Stack spacing={3}>
        <Card>
          <CardHeader title="Campaign Builder" subheader="Select zips and push them to Facebook ad sets" />
          <Divider />
          <CardContent>
            <Stack spacing={3}>
              {clientsError && <Alert severity="error">{clientsError}</Alert>}
              <Stack
                marginTop={1}
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                sx={{ alignItems: { xs: 'stretch', md: 'flex-start' } }}
              >
                <Box sx={{ flex: 2 }}>
                  <ClientDropdown
                    value={selectedClientId || ''}
                    onChange={(value) => setSelectedClientId(value || null)}
                    label="Client"
                  />
                </Box>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControl fullWidth>
                    <InputLabel id="ad-account-select-label">Facebook Ad Account</InputLabel>
                    <Select
                      labelId="ad-account-select-label"
                      value={selectedAdAccountId || ''}
                      label="Facebook Ad Account"
                      onChange={(event) => setSelectedAdAccountId(event.target.value)}
                      disabled={structureLoading || !accountOptions.length}
                    >
                      {accountOptions.map((account) => (
                        <MenuItem key={account.account_id} value={account.account_id}>
                          {account.account_name || account.account_id}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {structureLoading && <CircularProgress size={18} />}
                </Box>
              </Stack>
              <Stack marginTop={1} direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
                  <FormControl fullWidth>
                    <InputLabel id="campaign-select-label">Campaign</InputLabel>
                    <Select
                      labelId="campaign-select-label"
                      multiple
                      value={selectedCampaignIds}
                      label="Campaign"
                      onChange={(event) => setSelectedCampaignIds(event.target.value)}
                      disabled={!campaignOptions.length || structureLoading}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((id) => {
                            const campaign = campaignOptions.find(
                              (item) => item.campaign_id === id
                            );
                            const label = campaign
                              ? `${campaign.campaign_name || `Campaign ${campaign.campaign_id}`} ${
                                  campaign.status ? `(${campaign.status})` : ''
                                }`.trim()
                              : id;
                            return (
                              <Chip key={id} label={label} size="small" />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {campaignOptions.map((campaign) => {
                        const label = `${campaign.campaign_name || `Campaign ${campaign.campaign_id}`} ${
                          campaign.status ? `(${campaign.status})` : ''
                        }`.trim();
                        return (
                          <MenuItem key={campaign.campaign_id} value={campaign.campaign_id}>
                            <Checkbox checked={selectedCampaignIds.includes(campaign.campaign_id)} />
                            <ListItemText primary={label} />
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                  {structureLoading && <CircularProgress size={18} />}
                </Stack>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showInactiveCampaigns}
                      onChange={(event) => setShowInactiveCampaigns(event.target.checked)}
                      size="small"
                    />
                  }
                  label="Show inactive campaigns"
                  sx={{ alignSelf: 'center', marginLeft: 1 }}
                />
                <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
                  <FormControl fullWidth>
                    <InputLabel id="ad-set-select-label">Ad Set</InputLabel>
                    <Select
                      labelId="ad-set-select-label"
                      multiple
                      value={selectedAdSetIds}
                      label="Ad Set"
                      onChange={(event) => setSelectedAdSetIds(event.target.value)}
                      disabled={!adSetOptions.length || structureLoading}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((id) => {
                            const adSet = adSetOptions.find((item) => item.ad_set_id === id);
                            const label = adSet
                              ? `${adSet.ad_set_name || `Ad Set ${adSet.ad_set_id}`} ${
                                  adSet.active ? '' : '(Inactive)'
                                }`.trim()
                              : id;
                            return <Chip key={id} label={label} size="small" />;
                          })}
                        </Box>
                      )}
                    >
                      {adSetOptions.map((adSet) => {
                        const label = `${adSet.ad_set_name || `Ad Set ${adSet.ad_set_id}`} ${
                          adSet.active ? '' : '(Inactive)'
                        }`.trim();
                        return (
                          <MenuItem key={adSet.ad_set_id} value={adSet.ad_set_id}>
                            <Checkbox checked={selectedAdSetIds.includes(adSet.ad_set_id)} />
                            <ListItemText primary={label} />
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                  {structureLoading && <CircularProgress size={18} />}
                </Stack>
              </Stack>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                sx={{ alignItems: 'stretch' }}
              >
                <Autocomplete
                  multiple
                  options={verticals}
                  getOptionLabel={(option) => option.vertical || ''}
                  value={verticals.filter((vert) => selectedVerticalIds.includes(vert.id))}
                  onChange={(_, value) => setSelectedVerticalIds(value.map((item) => item.id))}
                  loading={verticalsLoading}
                  disableCloseOnSelect
                  sx={{ flex: 1, minWidth: 200 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Verticals"
                      placeholder="Select verticals"
                      fullWidth
                    />
                  )}
                />
                <Autocomplete
                  multiple
                  options={territories}
                  getOptionLabel={(option) => {
                    const name = option.name || option.territory || '';
                    const count = option.active_zip_count || 0;
                    return `${name} (${count} zips)`;
                  }}
                  value={territories.filter((territory) => selectedTerritoryIds.includes(territory.id))}
                  onChange={(_, value) => setSelectedTerritoryIds(value.map((item) => item.id))}
                  loading={territoriesLoading}
                  disableCloseOnSelect
                  sx={{ flex: 1, minWidth: 200 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Territories"
                      placeholder="Select territories"
                      fullWidth
                    />
                  )}
                />
              </Stack>
              <Stack direction="row" spacing={1} sx={{ marginTop: 1 }}>
                <Button
                  color="primary"
                  size="small"
                  variant="outlined"
                  onClick={() => loadBudgetTerritoryIdsForCurrentMonth()}
                  disabled={!territories.length || budgetTerritoriesLoading}
                >
                  Budgeted Territories (This Month)
                </Button>
                <Button
                  color="primary"
                  size="small"
                  variant="outlined"
                  onClick={() => setSelectedTerritoryIds(territories.filter((t) => (t.active_zip_count || 0) > 0).map((t) => t.id))}
                  disabled={!territories.some((t) => (t.active_zip_count || 0) > 0)}
                >
                  All Territories
                </Button>
                <Button
                  color="primary"
                  size="small"
                  variant="text"
                  onClick={() => setSelectedTerritoryIds([])}
                  disabled={!selectedTerritoryIds.length}
                >
                  Clear selection
                </Button>
              </Stack>
              <Stack spacing={1}>
                <Typography variant="subtitle2">
                  Territory Budgets ({budgetMonthLabel || 'Current month'})
                </Typography>
                {budgetTerritoriesLoading ? (
                  <CircularProgress size={18} />
                ) : territoryBudgetItems.length ? (
                  <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Territory</TableCell>
                          <TableCell align="right">Budget</TableCell>
                          <TableCell align="center">Selected</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {territoryBudgetItems.map((item) => (
                          <TableRow key={`territory-budget-${item.id}`}>
                            <TableCell>{item.name}</TableCell>
                            <TableCell align="right">{USD_CURRENCY_FORMATTER.format(item.budget)}</TableCell>
                            <TableCell align="center">
                              {selectedTerritoryIds.includes(item.id) ? 'Yes' : 'No'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="text.secondary">
                    No territory budgets over $0 found for this month.
                  </Typography>
                )}
              </Stack>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeFilteredOut}
                    onChange={(event) => setIncludeFilteredOut(event.target.checked)}
                  />
                }
                label="Include filtered-out ZIPs"
              />
              <Divider />
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Stack>
                  <Typography variant="subtitle2">ZIP preview</Typography>
                  {previewLoading ? (
                    <CircularProgress size={20} />
                  ) : preview.zip_count ? (
                    <Typography variant="h5">{preview.zip_count.toLocaleString()} ZIPs selected</Typography>
                  ) : (
                    <Typography color="text.secondary">No ZIPs selected yet.</Typography>
                  )}
                </Stack>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handlePush}
                  disabled={
                    !selectedClientId ||
                    !selectedAdAccountId ||
                    !selectedAdSetIds.length ||
                    pushLoading ||
                    !preview.zip_count
                  }
                  endIcon={pushLoading ? <CircularProgress size={20} /> : null}
                >
                  Push ZIPs to Facebook
                </Button>
                {lastPush && (
                  <Button
                    variant="outlined"
                    color="success"
                    component="a"
                    href={buildFacebookAdsManagerUrl(lastPush.adAccountId, lastPush.adSetIds)}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<OpenInNewIcon />}
                  >
                    View in Facebook
                  </Button>
                )}
              </Stack>
              <Divider />
              <Stack spacing={1}>
                <Typography variant="subtitle2">Current selected Ad Set targeting</Typography>
                {selectedAdSetIds.length !== 1 ? (
                  <Typography color="text.secondary">
                    Select exactly one ad set to inspect its current Facebook targeting.
                  </Typography>
                ) : adSetTargetingLoading ? (
                  <CircularProgress size={20} />
                ) : adSetTargeting ? (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Direct zips: {adSetTargeting.direct_zip_count} | Cluster zips (from DB):{' '}
                      {adSetTargeting.db_cluster_zip_count}
                    </Typography>
                    {adSetTargeting.facebook_location_cluster_ids?.length ? (
                      <Typography variant="body2" color="text.secondary">
                        Facebook Cluster IDs: {adSetTargeting.facebook_location_cluster_ids.join(', ')}
                      </Typography>
                    ) : null}
                    {adSetTargeting.db_location_cluster_ids?.length ? (
                      <Typography variant="body2" color="text.secondary">
                        DB Cluster IDs: {adSetTargeting.db_location_cluster_ids.join(', ')}
                      </Typography>
                    ) : null}
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {(adSetTargeting.direct_zip_codes || []).slice(0, 20).map((zip) => (
                        <Chip key={`direct-${zip}`} label={zip} size="small" />
                      ))}
                      {(adSetTargeting.db_cluster_zip_codes || []).slice(0, 20).map((zip) => (
                        <Chip key={`cluster-${zip}`} label={zip} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </>
                ) : (
                  <Typography color="text.secondary">No targeting data found for this ad set.</Typography>
                )}
              </Stack>
              {previewError && <Alert severity="error">{previewError}</Alert>}
              {adSetTargetingError && <Alert severity="error">{adSetTargetingError}</Alert>}
              {pushError && <Alert severity="error">{pushError}</Alert>}
              <Divider />
              <Stack spacing={1}>
                <Typography variant="subtitle2">Session debug log</Typography>
                {frontendLogs.length ? (
                  <Stack spacing={1}>
                    {frontendLogs.map((entry) => (
                      <Box
                        key={entry.id}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 1,
                        }}
                      >
                        <Typography variant="body2">
                          {new Date(entry.at).toLocaleString()} • {entry.message}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                        >
                          {JSON.stringify(entry.meta)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography color="text.secondary">No session logs yet.</Typography>
                )}
              </Stack>
              <Divider />
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle2">ZIP push log</Typography>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() =>
                      loadPushLog({
                        adSetId: selectedAdSetIds.length === 1 ? selectedAdSetIds[0] : undefined,
                      })
                    }
                    disabled={pushLogLoading || !selectedClientId}
                  >
                    Refresh
                  </Button>
                </Stack>
                {pushLogLoading ? (
                  <CircularProgress size={20} />
                ) : pushLogError ? (
                  <Alert severity="error">{pushLogError}</Alert>
                ) : pushLog.length ? (
                  <Stack spacing={1}>
                    {pushLog.map((entry) => (
                      <Stack
                        key={`${entry.ad_set_id}-${entry.updated_at}`}
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1}
                        justifyContent="space-between"
                      >
                        <Stack spacing={0.5}>
                          <Typography variant="body2">
                            {(entry.ad_account_name || entry.ad_account_id || 'Ad Account')} •{' '}
                            {entry.campaign_name || entry.campaign_id || 'Campaign'} •{' '}
                            {entry.ad_set_name || entry.ad_set_id}
                          </Typography>
                          {(entry.verticals?.length || entry.territories?.length) ? (
                            <Typography variant="caption" color="text.secondary">
                              {(entry.verticals || []).join(', ') || 'No verticals'} •{' '}
                              {(entry.territories || []).join(', ') || 'No territories'}
                            </Typography>
                          ) : null}
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            {entry.zip_count} zips • {entry.cluster_id || 'no cluster'} •{' '}
                            {entry.updated_at
                              ? new Date(entry.updated_at).toLocaleString()
                              : 'unknown time'}
                          </Typography>
                          {entry.ad_account_id && entry.ad_set_id && (
                            <Button
                              size="small"
                              variant="text"
                              component="a"
                              href={buildFacebookAdsManagerUrl(entry.ad_account_id, [entry.ad_set_id])}
                              target="_blank"
                              rel="noopener noreferrer"
                              startIcon={<OpenInNewIcon fontSize="small" />}
                              sx={{ minWidth: 'auto', textTransform: 'none' }}
                            >
                              View
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  <Typography color="text.secondary">No pushes recorded yet.</Typography>
                )}
              </Stack>
              {preview.sample_zip_codes.length > 0 && (
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {previewChips}
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}

export default CampaignBuilderPage;
